import { useState, useMemo, useRef, useEffect } from "react";
import { Copy, ChevronUp, ChevronDown } from "lucide-react";

interface ResultTableProps {
  rows: Record<string, unknown>[];
  columns: string[];
  onCopyMarkdown: () => void;
  isMobile?: boolean;
  /** Не показывать кнопку копирования (когда заголовок с кнопкой рисуется снаружи) */
  hideCopyButton?: boolean;
}

const MAX_CELL_LENGTH = 80; // Максимальная длина ячейки до обрезки (компактный вид)

/**
 * Форматирует значение для отображения в таблице.
 * Поддерживает массивы, кортежи и другие сложные типы.
 */
export const formatCellValue = (value: unknown): string => {
  if (value === null || value === undefined) {
    return "";
  }

  // Если это массив
  if (Array.isArray(value)) {
    // Проверяем, является ли это пустым массивом
    if (value.length === 0) {
      return "[]";
    }
    return `[${value.map(v => formatCellValue(v)).join(", ")}]`;
  }

  // Если это строка, которая может быть JSON-массивом или кортежем
  if (typeof value === "string") {
    // Пытаемся распарсить как JSON
    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed)) {
        if (parsed.length === 0) {
          return "[]";
        }
        return `[${parsed.map(v => formatCellValue(v)).join(", ")}]`;
      }
      // Если это объект (именованный кортеж), форматируем как кортеж
      if (typeof parsed === "object" && parsed !== null && !Array.isArray(parsed)) {
        const entries = Object.entries(parsed);
        if (entries.length === 0) {
          return "()";
        }
        return `(${entries.map(([k, v]) => `${k}: ${formatCellValue(v)}`).join(", ")})`;
      }
    } catch {
      // Не JSON, проверяем другие форматы
    }

    // Проверяем, является ли строка представлением кортежа в формате (a, b, c)
    // Используем более точное регулярное выражение
    if (value.trim().startsWith("(") && value.trim().endsWith(")")) {
      return value; // Уже в формате кортежа
    }

    // Проверяем, является ли строка представлением массива в формате [a, b, c]
    if (value.trim().startsWith("[") && value.trim().endsWith("]")) {
      return value; // Уже в формате массива
    }

    return value;
  }

  // Если это объект (но не массив) - это может быть именованный кортеж
  if (typeof value === "object" && value !== null) {
    const entries = Object.entries(value);
    if (entries.length === 0) {
      return "()";
    }
    // Форматируем как именованный кортеж
    return `(${entries.map(([k, v]) => `${k}: ${formatCellValue(v)}`).join(", ")})`;
  }

  // Для примитивных типов
  return String(value);
};

// Компонент для ячейки с обрезкой и tooltip
const TableCell = ({ value, column }: { value: unknown; column: string }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const formattedValue = formatCellValue(value);
  const isLong = formattedValue.length > MAX_CELL_LENGTH;
  const displayValue = isLong && !isExpanded 
    ? formattedValue.substring(0, MAX_CELL_LENGTH) + "..." 
    : formattedValue;

  return (
    <>
      <div className="flex items-start gap-2 w-full h-full min-h-[32px] py-1.5">
        <span 
          className="font-mono text-sm whitespace-pre-wrap break-words flex-1 overflow-hidden"
          style={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}
        >
          {displayValue}
        </span>
        {isLong && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              setIsExpanded(!isExpanded);
            }}
            className="text-primary hover:text-primary/80 text-xs font-medium flex-shrink-0 px-1 mt-0.5"
            title={isExpanded ? "Свернуть" : "Показать полностью"}
          >
            {isExpanded ? "Свернуть" : "Показать"}
          </button>
        )}
      </div>
      {/* Tooltip при наведении на длинный текст */}
      {isLong && !isExpanded && (
        <div className="absolute left-0 top-full mt-1 z-50 hidden group-hover:block bg-neutral-text text-white text-xs rounded-lg px-3 py-2 shadow-xl max-w-md break-words pointer-events-none whitespace-pre-wrap">
          {formattedValue}
          <div className="absolute -top-1 left-4 w-2 h-2 bg-neutral-text rotate-45"></div>
        </div>
      )}
    </>
  );
};

export const ResultTable = ({ rows, columns, onCopyMarkdown, isMobile = false, hideCopyButton = false }: ResultTableProps) => {
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<"asc" | "desc" | null>(null);
  const tableRef = useRef<HTMLDivElement>(null);
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>({});

  // Если нет данных, показываем пустую таблицу с заголовками
  const isEmpty = !rows || rows.length === 0;

  const sortedRows = useMemo(() => {
    if (!sortColumn || !sortDirection) return rows;

    return [...rows].sort((a, b) => {
      const valA = a[sortColumn];
      const valB = b[sortColumn];

      if (valA === valB) return 0;

      if (typeof valA === "number" && typeof valB === "number") {
        return sortDirection === "asc" ? valA - valB : valB - valA;
      }

      return sortDirection === "asc"
        ? String(valA ?? "").localeCompare(String(valB ?? ""))
        : String(valB ?? "").localeCompare(String(valA ?? ""));
    });
  }, [rows, sortColumn, sortDirection]);

  const handleSort = (col: string) => {
    if (sortColumn !== col) {
      setSortColumn(col);
      setSortDirection("asc");
    } else if (sortDirection === "asc") {
      setSortDirection("desc");
    } else if (sortDirection === "desc") {
      setSortColumn(null);
      setSortDirection(null);
    }
  };

  // Вычисляем адаптивные ширины колонок на основе содержимого
  useEffect(() => {
    if (isEmpty || isMobile || !tableRef.current) {
      setColumnWidths({});
      return;
    }

    const containerWidth = tableRef.current.clientWidth;
    if (containerWidth === 0) return;

    const widths: Record<string, number> = {};
    const minWidths: Record<string, number> = {};
    let totalMinWidth = 0;
    
    // Сначала вычисляем минимальные ширины для каждой колонки
    columns.forEach((col) => {
      // Минимальная ширина на основе длины названия колонки (компактнее)
      let minWidth = col.length * 8 + 70;
      
      const sampleRows = sortedRows.slice(0, 20);
      sampleRows.forEach((row) => {
        const formatted = formatCellValue(row[col]);
        const displayLength = formatted.length > MAX_CELL_LENGTH 
          ? MAX_CELL_LENGTH + 3
          : formatted.length;
        const estimatedWidth = displayLength * 6.5 + 48;
        minWidth = Math.max(minWidth, estimatedWidth);
      });
      
      minWidths[col] = Math.min(minWidth, 360);
      totalMinWidth += minWidths[col];
    });

    // Если сумма минимальных ширин больше ширины контейнера, используем минимальные ширины
    // и позволяем горизонтальную прокрутку
    if (totalMinWidth > containerWidth) {
      // Используем минимальные ширины, таблица будет прокручиваться горизонтально
      columns.forEach((col) => {
        widths[col] = minWidths[col];
      });
    } else {
      // Иначе используем минимальные ширины и распределяем оставшееся пространство пропорционально
      const remainingWidth = containerWidth - totalMinWidth;
      columns.forEach((col) => {
        widths[col] = minWidths[col] + (remainingWidth / columns.length);
      });
    }

    setColumnWidths(widths);
  }, [columns, sortedRows, isEmpty, isMobile]);

  if (isMobile) {
    return (
      <div className="space-y-3">
        {!isEmpty && !hideCopyButton && (
          <div className="flex justify-end">
            <button
              onClick={onCopyMarkdown}
              className="flex items-center justify-center w-8 h-8 rounded bg-neutral-light border border-neutral-border hover:bg-primary/10 active:scale-95 transition"
              title="Копировать в Markdown"
              aria-label="Копировать в Markdown"
            >
              <Copy size={14} className="text-neutral-text" />
            </button>
          </div>
        )}
        {isEmpty ? (
          <div className="border border-neutral-border bg-neutral-light px-3 py-4 text-center text-neutral-text/60 rounded">
            Нет данных
          </div>
        ) : (
          sortedRows.map((r, idx) => (
            <div key={idx} className="border border-neutral-border rounded-lg bg-white shadow-sm p-3 space-y-2 w-full">
              <div className="text-xs text-neutral-text/60 truncate">Строка {idx + 1}</div>
              {columns.map((c) => (
                <div key={c} className="flex justify-between gap-3 py-1 border-b border-neutral-border/60 last:border-b-0">
                  <span className="text-sm font-semibold text-neutral-text">{c}</span>
                  <span className="text-sm text-neutral-text text-right break-all font-mono">{formatCellValue(r[c])}</span>
                </div>
              ))}
            </div>
          ))
        )}
      </div>
    );
  }

  return (
    <div 
      ref={tableRef}
      className="relative bg-white soft-scrollbar"
      style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: '100%', width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}
    >
      <table className="bg-white border-collapse" style={{ tableLayout: 'auto', width: 'max-content', minWidth: '100%', boxSizing: 'border-box' }}>
          {/* Фиксированный заголовок */}
          <thead className="bg-white sticky top-0 z-10">
            <tr>
              {columns.map((c, idx) => (
                <th
                  key={c}
                  className="px-4 py-2.5 text-left font-semibold text-neutral-text cursor-pointer select-none hover:bg-[#F6F6F7] transition-colors h-10 border-b border-[#E9E9E9]"
                  onClick={() => handleSort(c)}
                  style={{ 
                    width: columnWidths[c] ? `${columnWidths[c]}px` : 'auto',
                    minWidth: columnWidths[c] ? `${columnWidths[c]}px` : `${c.length * 8 + 70}px`,
                    maxWidth: columnWidths[c] ? `${columnWidths[c]}px` : "360px",
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    boxSizing: 'border-box'
                  }}
                >
                  <div className="flex items-center justify-between h-full min-w-0">
                    <div className="flex items-center gap-1 min-w-0 flex-1">
                      <span className="truncate">{c}</span>
                      <div className="flex flex-col ml-1 flex-shrink-0">
                        <ChevronUp
                          size={12}
                          className={`transition-colors ${
                            sortColumn === c && sortDirection === "asc"
                              ? "text-primary"
                              : "text-neutral-text/40"
                          }`}
                        />
                        <ChevronDown
                          size={12}
                          className={`transition-colors ${
                            sortColumn === c && sortDirection === "desc"
                              ? "text-primary"
                              : "text-neutral-text/40"
                          }`}
                        />
                      </div>
                    </div>
                    {idx === columns.length - 1 && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onCopyMarkdown();
                        }}
                        className="flex items-center gap-1 px-2 py-1 rounded hover:bg-primary/10 text-sm ml-2 transition-colors"
                        title="Копировать в Markdown"
                      >
                        <Copy size={16} className="text-neutral-text" />
                      </button>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {isEmpty ? (
              <tr>
                <td 
                  className="bg-white px-4 py-8 text-center text-neutral-text/50" 
                  colSpan={columns.length}
                >
                  Нет данных
                </td>
              </tr>
            ) : (
              <>
                {sortedRows.map((r, idx) => (
                  <tr 
                    key={idx} 
                    className="bg-white hover:bg-[#F6F6F7] transition-colors border-b border-[#E9E9E9] last:border-b-0"
                  >
                    {columns.map((c, colIdx) => (
                      <td 
                        key={c} 
                        className="px-4 py-0 text-neutral-text relative group border-r border-[#EFEFEF] last:border-r-0 bg-white align-top"
                        style={{
                          width: columnWidths[c] ? `${columnWidths[c]}px` : 'auto',
                          minWidth: columnWidths[c] ? `${columnWidths[c]}px` : `${c.length * 8 + 70}px`,
                          maxWidth: columnWidths[c] ? `${columnWidths[c]}px` : "360px",
                          overflow: 'hidden',
                          boxSizing: 'border-box'
                        }}
                      >
                        <TableCell value={r[c]} column={c} />
                      </td>
                    ))}
                  </tr>
                ))}
              </>
            )}
          </tbody>
      </table>
    </div>
  );
};
